import {useContext, MutableRefObject, useEffect, useState} from 'react';
import {useTypedFastStorage} from '../../../hooks/useFastStorage';
import useTableStorage from '../../../hooks/useTableStorage';
import {IGeneralWithList} from "../../../ifmsTypes";
import { ApplicableTableSettings } from '../../../tableSettings';
import SecondaryButton from "../../../ui/SecondaryButton";
import SmartTable from '../../../uiControls/SmartTable';
import { adjustBalanceTable } from './reportTableSettings/reportTableSettings';
import { DateRenderer, FloatRenderer, NumberedIndicatorRenderer, TextAlignLeftRenderer } from '../../../uiControls/SmartTableRenderers';
import ApplicationContext from '../../../ApplicationContext';
import { getTbNameByJobName, JobNames } from '../../../framework.core/JobNames';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import constants from '../../../constants';

interface AdjustBalanceStorageKeys {
  modelStorage: string,
};

interface AdjustBalanceViewParams {
  id: string | undefined,
  onPopupClose?: () => void,
  jobName: JobNames
};
interface AdjustBalanceLoadedDetailsViewParams {
  adjustBalance: IGeneralWithList,
  adjustBalanceRef: MutableRefObject<IGeneralWithList | null>,
  setAdjustBalance: (x: Partial<IGeneralWithList>) => void,
  onPopupClose?: () => void,
  jobName: JobNames
};

function getAdjustBalanceStorageKeys(): AdjustBalanceStorageKeys {
  return {modelStorage: 'report-adjust-balance'}
}

function AdjustBalanceImpl(params: AdjustBalanceViewParams) {
  const {jobName} = params;
  const {modelStorage} = getAdjustBalanceStorageKeys();
  const [inStorage, setInStorage, removeAdjustBalanceFromStorage, adjustBalanceRef] = useTypedFastStorage<IGeneralWithList>(modelStorage, null);
  const [initialized, setInitialized] = useState(false);
  
  useEffect(() => {
      initialize();
      return () => {
        removeAdjustBalanceFromStorage()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  async function loadExisting() {
    return {
    }
  }

  async function initialize() {
    if (!inStorage) {
      const data = await loadExisting();
      setInStorage({...data})
    }
    setInitialized(true);
  }

  if (!initialized || !inStorage) {
    return null;
  }

  return (
    <AdjustBalanceInitializedImpl
      adjustBalance={inStorage}
      adjustBalanceRef={adjustBalanceRef}
      setAdjustBalance={setInStorage}
      onPopupClose={params.onPopupClose}
      jobName={jobName}
    />
  );
}

function AdjustBalanceInitializedImpl({
  adjustBalance,
  adjustBalanceRef,
  setAdjustBalance,
  onPopupClose,
  jobName
}: AdjustBalanceLoadedDetailsViewParams) {
  const tableStorage = useTableStorage();
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [check, setCheck] = useState(true);
  const appContext = useContext(ApplicationContext);
  
  function onChange(change: Partial<IGeneralWithList>) {
    setAdjustBalance(change);
  }

  const tableOptions: ApplicableTableSettings = {
    tableName: 'adjustBalanceTable',
    ...adjustBalanceTable,
    data: adjustBalance.list,
    
    rowRenderers: [
      {accessor: 'indicator', Cell: NumberedIndicatorRenderer},
      {accessor: 'invoiceDate', Cell: DateRenderer },
      {accessor: 'invoiceNo',  Cell: TextAlignLeftRenderer},
      {accessor: 'invoiceAmt',  Cell: FloatRenderer},
      {accessor: 'detAmt',  Cell: FloatRenderer},
      {accessor: 'paidAmt',  Cell: FloatRenderer},
      {accessor: 'aPaidAmt',  Cell: FloatRenderer},
      {accessor: 'dutyPaidAmt',  Cell: FloatRenderer},
      {accessor: 'aDutyPaidAmt',  Cell: FloatRenderer},
      {accessor: 'errorAmt',  Cell: FloatRenderer},

    ],
    customStorage: tableStorage,
    headersColor: 'white',
    // footerRow: [ 
    //    {
    //      colspan:9,
    //      renderer:TotalAmountRenderer
    //    }
    // ],
    height: '420px',
  }; 
  
//  function TotalAmountRenderer() {
//     let totalErrorAmt = 0;
//     if(adjustBalance?.list){
//       totalErrorAmt = adjustBalance.list.map(x => x.errorAmt).reduce((acc: number, x) => acc + x , 0)
//       return <Float decimals={2} style={{color: 'blue', textAlign:'left',  backgroundColor: 'white'}} disabled value={totalErrorAmt} />; 
//     }
//     else {
//       return <Float decimals={2} disabled dark value={null} />; 
//     }  
//   }

  async function subFill(){
    const apiResult = await appContext.api.subFill(getTbNameByJobName(jobName),getAdjTypeByJobName(jobName));
    if(apiResult && apiResult.success){
      if(apiResult.result.length > 0){
        setCheck(false);
        onChange({list: apiResult.result})
      }
      else{
        setCheck(true);
        await showGlobalOkDialog("No Error Account List");
      }
    }
    else{
      await showGlobalOkDialog(apiResult?.message ?? constants.LOAD_ERROR_MESSAGE);
    }
  }

  async function subUpdate(){
    const apiResult = await appContext.api.subUpdate(getTbNameByJobName(jobName));
    if(!apiResult || !apiResult.success){
      await showGlobalOkDialog(apiResult?.message ?? constants.LOAD_ERROR_MESSAGE);
    } else{
      onChange({list:[]})
      setCheck(true);
    }
  }

  function getAdjTypeByJobName(jobName: string){
    switch(jobName){
        case JobNames.Invoice:                  
        case JobNames.InvoAdj:                                     
        case JobNames.AutoBillingStorage:       
        case JobNames.AutoBillingInvoice:               return "AdjustInvo";

        case JobNames.GInvo:                            return "AdjustGInvo";

        case JobNames.Bill:                             return "AdjustAP";

        case JobNames.GBill:                            return 'AdjustGAP';

        case JobNames.WHStorageInv:                     return 'AdjustStorage';

        default : return '';
    }
  }

  return (
    <div className='report-popup'>
      <div>
        <SmartTable {...tableOptions} />
      </div>
      
      <div className='report-footer'>
        <SecondaryButton
          size="regular"
          style={{width: '90px'}}
          onClick={subFill}
        >
          READ ERROR
        </SecondaryButton>
        <SecondaryButton
          size="regular"
          style={{width: '90px'}}

          hidden={check}
          onClick={subUpdate}
        >
          UPDATE
        </SecondaryButton>

        <SecondaryButton
          size="regular"
          style={{width: '90px'}}
          onClick={onPopupClose}>
          CLOSE
        </SecondaryButton>
      </div>
    </div>
  );
}

const AdjustBalance = ({
  onPopupClose, 
  jobName
}: {
  onPopupClose?: () => void, jobName : JobNames
}) => {
  // const { id } = useParams<AdjustBalanceViewParams>();

  return (
    <AdjustBalanceImpl id={''} jobName={jobName} onPopupClose={onPopupClose} key={`adjustBalance-details`} />
  );
};
export default AdjustBalance;
